import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Subject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Interface pour les événements de nouvelle commande
 */
export interface NewOrderEvent {
  orderId: string;
  orderNumber: string;
  userId: string;
  userName: string;
  timestamp: number;
}

/**
 * Interface pour les événements de changement de statut de commande
 */
export interface OrderStatusChangeEvent {
  orderId: string;
  orderNumber: string;
  oldStatus: string;
  newStatus: string;
  timestamp: number;
}

/**
 * Interface pour les événements de nouvelle mise à disposition
 */
export interface NewMiseADispoEvent {
  miseADispoId: string;
  productName: string;
  quantity: number;
  userId: string;
  userName: string;
  timestamp: number;
}

/**
 * Service de gestion WebSocket pour les notifications en temps réel de l'admin
 *
 * Ce service gère la connexion WebSocket avec le backend et expose
 * des observables pour écouter les événements en temps réel.
 *
 * Architecture:
 * - Connexion persistante pendant toute la session admin
 * - Authentification via JWT token
 * - Reconnexion automatique en cas de perte de connexion
 * - Émission d'événements via RxJS Subjects
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket: Socket | null = null;
  private newOrderSubject = new Subject<NewOrderEvent>();
  private orderStatusChangeSubject = new Subject<OrderStatusChangeEvent>();
  private newMiseADispoSubject = new Subject<NewMiseADispoEvent>();

  /**
   * Observable pour écouter les nouvelles commandes
   */
  public newOrder$: Observable<NewOrderEvent> = this.newOrderSubject.asObservable();

  /**
   * Observable pour écouter les changements de statut de commande
   */
  public orderStatusChange$: Observable<OrderStatusChangeEvent> = this.orderStatusChangeSubject.asObservable();

  /**
   * Observable pour écouter les nouvelles mises à disposition
   */
  public newMiseADispo$: Observable<NewMiseADispoEvent> = this.newMiseADispoSubject.asObservable();

  constructor() {}

  /**
   * Établit une connexion WebSocket avec le backend
   *
   * @param token - Token JWT pour l'authentification
   *
   * Configuration:
   * - Namespace: /admin
   * - Transports: WebSocket (préféré) puis polling (fallback)
   * - Reconnexion automatique: 5 tentatives avec backoff exponentiel (1-5s)
   * - Timeout: 10 secondes
   *
   * Événements écoutés:
   * - connect: Connexion établie
   * - disconnect: Déconnexion
   * - connect_error: Erreur de connexion
   * - error: Erreur générale (notamment auth failed)
   * - order:new: Nouvelle commande créée
   * - order:status-change: Changement de statut de commande
   * - mise-a-dispo:new: Nouvelle mise à disposition créée
   */
  async connect(token: string): Promise<void> {
    if (!token) {
      console.warn('[WebSocketService Admin] No token provided for connection');
      return;
    }

    // Déconnecter si déjà connecté
    if (this.socket?.connected) {
      this.disconnect();
    }

    // Créer la connexion WebSocket
    const socketUrl = `${environment.apiUrl}/admin`;

    this.socket = io(socketUrl, {
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000
    });

    // Gestionnaires d'événements de connexion
    this.socket.on('connect', () => {
      console.log('[WebSocket Admin] Connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('[WebSocket Admin] Disconnected:', reason);
      if (reason === 'io server disconnect') {
        // Le serveur a forcé la déconnexion (probablement auth failed)
        console.warn('[WebSocket Admin] Disconnected by server - authentication may have failed');
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('[WebSocket Admin] Connection error:', error.message);
    });

    this.socket.on('error', (error: any) => {
      console.error('[WebSocket Admin] Error:', error);
      if (error.message === 'Authentication failed') {
        // Token invalide ou expiré - déconnecter proprement
        this.disconnect();
      }
    });

    // Gestionnaires d'événements métier
    this.socket.on('order:new', (data: NewOrderEvent) => {
      console.log('[WebSocket Admin] New order event received:', data);
      this.newOrderSubject.next(data);
    });

    this.socket.on('order:status-change', (data: OrderStatusChangeEvent) => {
      console.log('[WebSocket Admin] Order status change event received:', data);
      this.orderStatusChangeSubject.next(data);
    });

    this.socket.on('mise-a-dispo:new', (data: NewMiseADispoEvent) => {
      console.log('[WebSocket Admin] New mise à dispo event received:', data);
      this.newMiseADispoSubject.next(data);
    });
  }

  /**
   * Ferme la connexion WebSocket
   *
   * Nettoie proprement la connexion et supprime tous les listeners
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      console.log('[WebSocket Admin] Disconnected manually');
    }
  }

  /**
   * Vérifie si le WebSocket est actuellement connecté
   *
   * @returns true si connecté, false sinon
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Retourne l'ID du socket actuellement connecté
   * Utile pour le debug
   *
   * @returns ID du socket ou null si non connecté
   */
  getSocketId(): string | null {
    return this.socket?.id ?? null;
  }
}
